import * as R from 'ramda';
import { intervalToDuration, differenceInMinutes } from 'date-fns';
// components
import { pickerColorGrey, pickerColorYellow } from '../../components/color-picker';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { ENUMS } from '../../constants/enums';
// feature drivers-card
import {
  CARD_HEIGHT_50,
  CARD_HEIGHT_90,
  CARD_HEIGHT_170,
  GROUP_TYPE_NONE,
} from './constants';
import {
  getGroupedAssignedLoadsRequest,
  getNotGroupedAssignedLoadsRequest,
} from './actions';
//////////////////////////////////////////////////

const getDuration = (firstEventDate, lastEventDate) => {
  try {
    const duration = intervalToDuration({
      end: G.getNewDate(lastEventDate),
      start: G.getNewDate(firstEventDate),
    });

    const {
      days = 0,
      hours = 0,
      years = 0,
      months = 0,
      minutes = 0,
      seconds = 0,
    } = duration;

    return {
      days,
      hours,
      years,
      months,
      minutes,
      seconds,
    };
  } catch (error) {
    return null;
  }
};

const getDifferenceInMinutes = (firstEventDate, lastEventDate) => {
  try {
    const difference = differenceInMinutes(G.getNewDate(firstEventDate), G.getNewDate(lastEventDate));

    return difference;
  } catch (error) {
    return null;
  }
};

const getSortedPeriodsFromDriverTelsAndUnavailablePeriods = (driver) => {
  const tels = G.getPropFromObject2('tels', driver, []);
  const unavailablePeriods = G.getPropFromObject2('unavailablePeriods', driver, []);

  const telsToUse = R.map(
    (item) => ({
      'lastEventLocation': R.path([GC.SYSTEM_OBJECT_LAST_EVENT, GC.FIELD_LOCATION], item),
      'lastEventLateDate': R.path([GC.SYSTEM_OBJECT_LAST_EVENT, GC.FIELD_LATE_DATE], item),
      'firstEventLocation': R.path([GC.SYSTEM_OBJECT_FIRST_EVENT, GC.FIELD_LOCATION], item),
      'firstEventEarlyDate': R.path([GC.SYSTEM_OBJECT_FIRST_EVENT, GC.FIELD_EARLY_DATE], item),
    }),
    tels,
  );

  const periodsToUse = R.map(
    (item) => ({
      'lastEventLocation': R.path([GC.FIELD_LOCATION], item),
      'lastEventLateDate': R.path([GC.FIELD_END_DATE], item),
      'firstEventLocation': R.path([GC.FIELD_LOCATION], item),
      'firstEventEarlyDate': R.path([GC.FIELD_START_DATE], item),
    }),
    unavailablePeriods,
  );

  const sortedPeriods = G.sortListByDate(R.concat(telsToUse, periodsToUse), 'firstEventEarlyDate');

  return sortedPeriods;
};

const getEmptyPeriodsFromDriverLoads = (driver, filters) => {
  const { dateTo, dateFrom } = filters;

  const sortedPeriods = getSortedPeriodsFromDriverTelsAndUnavailablePeriods(driver);

  if (G.isNilOrEmpty(sortedPeriods)) {
    return R.of(Array, {
      'firstEventDate': G.toStartOfDayFromDate(dateFrom),
      'lastEventDate': G.toEndOfDayFromDate(dateTo),
    });
  }

  const emptyPeriods = [];

  sortedPeriods.forEach((item, i, arr) => {
    const lastEventLateDate = G.getPropFromObject('lastEventLateDate', item);

    const item2 = R.path([R.inc(i)], arr);

    if (G.isZero(i)) {
      emptyPeriods.push({
        isFirst: true,
        'firstEventDate': G.toStartOfDayFromDate(dateFrom),
        'lastEventDate': R.or(G.getPropFromObject('firstEventEarlyDate', item), dateTo),
      });
    }

    if (G.isNilOrEmpty(item2)) {
      return emptyPeriods.push({
        isLast: true,
        'firstEventDate': lastEventLateDate,
        'lastEventDate': G.toEndOfDayFromDate(dateTo),
        'lastEventLocation': R.path(['lastEventLocation'], item),
        'firstEventLocation': R.path(['firstEventLocation'], item),
      });
    }

    const nextEventEarlyDate = G.getPropFromObject('firstEventEarlyDate', item2);

    if (G.isBeforeDate(nextEventEarlyDate, lastEventLateDate)) return;

    const duration = getDuration(lastEventLateDate, nextEventEarlyDate);

    if (G.isNilOrEmpty(duration)) return;

    const { days, hours } = duration;

    // TODO: check using here GC.UI_DRIVER_CARDS_DEFAULT_MIN_EMPTY_HOURS
    if (R.gt(R.add(hours, R.multiply(days, 24), 0))) {
      emptyPeriods.push({
        isLast: false,
        'firstEventDate': lastEventLateDate,
        'lastEventDate': nextEventEarlyDate,
        'lastEventLocation': R.path(['lastEventLocation'], item2),
        'firstEventLocation': R.path(['firstEventLocation'], item),
      });
    }
  });

  return emptyPeriods;
};

const mapDriverLoadsToEmptyPeriods = (driverLoads, minEmptyHours, filters) => {
  // TODO: check if we need using minEmptyHours
  // if (G.isNilOrEmpty(minEmptyHours)) return [];

  return R.compose(
    R.map((item) => R.assoc(
      'emptyPeriods',
      getEmptyPeriodsFromDriverLoads(item, filters),
      item,
    )),
  )(driverLoads);
};

const filterDriverLoadsByLocationAndDate = (
  items,
  locationResult,
  searchRadiusValue,
  searchDate,
) => {
  const searchDayMidday = G.toMidday(searchDate);

  const filtered = R.filter((item) => {
    const { emptyPeriods } = item;

    let filter = false;
    let filter2 = false;

    emptyPeriods.forEach((item) => {
      const { lastEventDate, firstEventDate, firstEventLocation, lastEventLocation } = item;

      if (R.and(G.isNotNilAndNotEmpty(locationResult), G.isNotNilAndNotEmpty(searchRadiusValue))) {
        if (G.isNotNilAndNotEmpty(firstEventLocation)) {
          const distance = G.getDistanceBetweenTwoPoints(locationResult, firstEventLocation);

          if (R.lt(distance, searchRadiusValue)) filter = true;
        }

        if (G.isNotNilAndNotEmpty(lastEventLocation)) {
          const distance = G.getDistanceBetweenTwoPoints(locationResult, lastEventLocation);

          if (R.lt(distance, searchRadiusValue)) filter = true;
        }
      }

      if (G.isAllTrue(G.isValidDate(searchDate), G.isValidDate(firstEventDate), G.isValidDate(lastEventDate))) {
        if (G.isBetween(searchDayMidday, firstEventDate, lastEventDate)) filter2 = true;

        if (G.isSameDay(searchDate, firstEventDate)) {
          const midnight = G.isMidnight(firstEventDate);

          const date = G.toStartOfDayFromDate(firstEventDate);

          if (R.and(G.isFalse(midnight), G.isAfterDate(searchDayMidday, date))) filter2 = true;
        }

        if (G.isSameDay(searchDate, lastEventDate)) {
          const midnight = G.isMidnight(lastEventDate);

          const date = G.toEndOfDayFromDate(lastEventDate);

          if (R.and(G.isFalse(midnight), G.isBeforeDate(searchDayMidday, date))) filter2 = true;
        }
      }
    });

    if (G.isAllTrue(
      G.isValidDate(searchDate),
      G.isNotNilAndNotEmpty(locationResult),
      G.isNotNilAndNotEmpty(searchRadiusValue),
    )) return R.and(filter, filter2);

    if (G.isValidDate(searchDate)) return filter2;

    return filter;
  }, items);

  return filtered;
};

const subtractOneFromNumber = (item) => R.subtract(item, 1);

const getDefaultFilterDates = (dayFilter) => {
  const initialFilterDays = subtractOneFromNumber(R.or(dayFilter, 7));
  const currentDate = G.getCurrentDateWithFormat(GC.DEFAULT_DATE_FORMAT);
  const dayToUse = R.divide(initialFilterDays, 2);

  const dateTo = G.addDateTimeWithFormat(
    currentDate,
    dayToUse,
    'days',
    GC.DEFAULT_DATE_FORMAT,
  );

  const dateFrom = G.subtractDateTimeWithFormat(
    currentDate,
    dayToUse,
    'days',
    GC.DEFAULT_DATE_FORMAT,
  );

  const minDate = G.subtractDateTimeWithFormat(
    dateFrom,
    dayToUse,
    'days',
    GC.DEFAULT_DATE_FORMAT,
  );

  const maxDate = G.subtractDateTimeWithFormat(
    dateTo,
    dayToUse,
    'days',
    GC.DEFAULT_DATE_FORMAT,
  );

  return { dateTo, dateFrom, minDate, maxDate, initialFilterDays: dayFilter };
};

const getDefaultFilterRequestDates = (dayFilter) => {
  const filters = getDefaultFilterDates(dayFilter);

  const { dateTo, dateFrom } = filters;

  return {
    filters,
    dateTo: G.toEndOfDayFromDate(dateTo),
    dateFrom: G.toStartOfDayFromDate(dateFrom),
  };
};

const getRequestFilters = (
  groupBy,
  filtersStore,
  referenceValue,
  globalFilterValue,
  getGroupedAssignedLoadsRequest,
  getNotGroupedAssignedLoadsRequest,
) => {
  const { filter, dateTo, dateFrom } = filtersStore;

  const action = G.ifElse(
    R.equals(groupBy, GROUP_TYPE_NONE),
    getNotGroupedAssignedLoadsRequest,
    getGroupedAssignedLoadsRequest,
  );

  return {
    action,
    options: {
      data: {
        filter,
        groupBy,
        referenceValue,
        globalFilterValue,
        dateTo: G.toEndOfDayFromDate(dateTo),
        dateFrom: G.toStartOfDayFromDate(dateFrom),
        currentEnterprise: G.getAmousCurrentBranchGuidFromWindow(),
      },
    },
  };
};

const getGuidsFromGroupedAssignedLoads = (loads) => R.compose(
  R.map(({ guid }) => guid),
  R.reduce((acc, group) => R.concat(acc, R.pathOr([], ['tels'], group)), []),
)(loads);

const getTelGuidsFromDriverCardListItems = (loads) => R.compose(
  R.map(({ guid }) => guid),
  R.reduce((acc, group) => R.concat(acc, R.pathOr([], ['tels'], group)), []),
)(loads);

const getMainCardWidth = (show24HoursView, zoom) => R.multiply(
  G.ifElse(G.isTrue(show24HoursView), 1200, 480),
  zoom,
);

const getCardDayMultiplier = (show24HoursView, zoom) => R.multiply(
  G.ifElse(G.isTrue(show24HoursView), 50, 20),
  zoom,
);

const white07Color = G.getTheme('colors.white07');
const mainDarkTransparent = G.getTheme('colors.mainDarkTransparent');

const getCardActionColor = (pickerColor) => {
  if (R.includes(pickerColor, [pickerColorGrey, pickerColorYellow])) {
    return mainDarkTransparent;
  }

  return white07Color;
};

const filterDriverLoadsByTeam = (teamFilterValue, driverLoads) => {
  if (G.isFalse(teamFilterValue)) {
    return driverLoads.filter(({ primaryDriverGuid }) => G.isNilOrEmpty(primaryDriverGuid));
  }

  return driverLoads;
};

const mapDriverLoadsWithFullSecondaryDriverInfo = (driverLoads) => {
  return driverLoads.map((driver) => {
    const { secondaryDriver } = driver;

    if (G.isNilOrEmpty(secondaryDriver)) return driver;

    const secondaryDriverGuid = G.getGuidFromObject(secondaryDriver);

    const searched = driverLoads.find((driver) => R.equals(G.getGuidFromObject(driver), secondaryDriverGuid));

    if (G.isNilOrEmpty(searched)) return driver;

    return R.assoc('secondaryDriver', searched, driver);
  });
};

const getAmousBranchesOptions = () => {
  const amousBranches = G.getItemFromWindow('amousBranches');

  return R.map((branch) => ({
    value: G.getGuidFromObject(branch),
    label: R.prop('enterpriseName', branch),
  }), R.values(amousBranches));
};

const getCardHeight = (cardHeight) => R.prop(cardHeight, {
  [ENUMS.ENUM_MINI]: CARD_HEIGHT_50,
  [ENUMS.ENUM_SMALL]: CARD_HEIGHT_90,
  [ENUMS.ENUM_FULL]: CARD_HEIGHT_170,
});

export {
  getDuration,
  getCardHeight,
  getMainCardWidth,
  getRequestFilters,
  getCardActionColor,
  getCardDayMultiplier,
  getDefaultFilterDates,
  subtractOneFromNumber,
  getDifferenceInMinutes,
  filterDriverLoadsByTeam,
  getAmousBranchesOptions,
  mapDriverLoadsToEmptyPeriods,
  getDefaultFilterRequestDates,
  getGuidsFromGroupedAssignedLoads,
  getTelGuidsFromDriverCardListItems,
  filterDriverLoadsByLocationAndDate,
  mapDriverLoadsWithFullSecondaryDriverInfo,
  getSortedPeriodsFromDriverTelsAndUnavailablePeriods,
};
